import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Check, Plus, LogOut, Search } from "lucide-react";

const SEARCH_THRESHOLD = 5;

function WorkspaceSwitcherMenu({
    triggerRef, companies, menuItems, onSwitch, onClose, onCreateWorkspace, onSignOut, navigate, location, t,
}) {
    const [query, setQuery] = useState("");
    const inputRef = useRef(null);
    const showSearch = companies.length >= SEARCH_THRESHOLD;

    const rect = triggerRef?.current?.getBoundingClientRect();
    const pos = rect
        ? { top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 240) }
        : { top: 0, left: 0, width: 240 };

    useEffect(() => {
        if (showSearch && inputRef.current) inputRef.current.focus();
    }, [showSearch]);

    const filtered = query
        ? companies.filter(({ company }) => company.name.toLowerCase().includes(query.toLowerCase()))
        : companies;

    const handleNavClick = (to) => {
        if (to === "/billing") localStorage.setItem("billing_return_url", location.pathname + location.search);
        onClose();
        navigate(to);
    };

    return ReactDOM.createPortal(
        <>
            <div className="fixed inset-0 z-40" onClick={onClose} />
            <div className="fixed z-50 bg-white rounded-xl shadow-lg border border-gray-200 py-1 overflow-hidden"
                style={{ top: pos.top, left: pos.left, width: pos.width }}>
                {showSearch && (
                    <div className="px-3 py-2">
                        <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                            <Search size={14} className="text-gray-400 shrink-0" />
                            <input ref={inputRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                                placeholder={t("sidebar.workspace.searchPlaceholder")}
                                className="bg-transparent text-sm outline-none w-full placeholder-gray-400" />
                        </div>
                    </div>
                )}
                {companies.length > 0 && (
                    <div className="max-h-[240px] overflow-y-auto py-1">
                        {filtered.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-gray-400 text-center">{t("sidebar.workspace.noResults")}</p>
                        ) : filtered.map(({ company, isActive }) => (
                            <button key={company.id} onClick={() => { if (!isActive) onSwitch(company.id); onClose(); }}
                                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left">
                                <div className="w-7 h-7 rounded-md bg-blue-600 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                                    {company.name[0]?.toUpperCase()}
                                </div>
                                <span className="text-sm text-gray-900 truncate flex-1">{company.name}</span>
                                {isActive && <Check size={16} className="text-blue-600 shrink-0" />}
                            </button>
                        ))}
                    </div>
                )}
                <button onClick={onCreateWorkspace} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left">
                    <Plus size={18} className="text-gray-500 ml-1" />
                    <span className="text-sm text-gray-700">{t("sidebar.workspace.newWorkspace")}</span>
                </button>
                <div className="border-t border-gray-100 my-1" />
                {menuItems.map((item) => (
                    <button key={item.key} onClick={() => handleNavClick(item.to)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left">
                        <item.icon size={18} className="text-gray-500 ml-1" />
                        <span className="text-sm text-gray-700">{item.label}</span>
                    </button>
                ))}
                <div className="border-t border-gray-100 my-1" />
                <button onClick={() => { onClose(); onSignOut(); }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left">
                    <LogOut size={18} className="text-gray-500 ml-1" />
                    <span className="text-sm text-gray-700">{t("sidebar.auth.signOut")}</span>
                </button>
            </div>
        </>,
        document.body
    );
}

export default WorkspaceSwitcherMenu;
